import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/ApiConfig';
import { useNavigate } from 'react-router-dom';
import Layout from './components/Layout';
import ResponsiveShadow from './components/ResponsiveShadow';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import Toolbar from './components/Toolbar';
import TableContainer from './components/TableContainer';
import TableHeader from './components/TableHeader';
import LoadingModal from './components/modals/LoadingModal';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import ConfirmModal from './components/modals/ConfirmModal';
import DefaultModal from './components/modals/DefaultModal';
import TipoObjetoModal from './components/modalServices/TipoObjetoModal';
import Pagination10 from './components/Pagination10';
import PageFooter from './components/PageFooter';
import usePagination10 from './components/hooks/usePagination10';
import './css/Cliente.css';

function TiposObjetos() {
  const [tiposObjetos, setTiposObjetos] = useState([]);
  const [atualizar, setAtualizar] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [filtro, setFiltro] = useState('');
  const [selectedTipoObjeto, setSelectedTipoObjeto] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTipoObjeto, setEditingTipoObjeto] = useState(null);

  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();

  // Filtrar tipos de objeto baseado no termo de busca
  const normalizeString = str => {
    return str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase();
  };

  const tiposObjetosFiltrados = tiposObjetos.filter(tipo => {
    const normalizedBusca = normalizeString(filtro);
    return (
      normalizeString(tipo.descricao || '').includes(normalizedBusca) ||
      tipo.id?.toString().includes(filtro)
    );
  });

  // Paginação dos tipos de objetos filtrados
  const totalItems = tiposObjetosFiltrados.length;
  const { setPagination } = usePagination10(totalItems);
  const selectedTiposObjetos = tiposObjetosFiltrados.slice(
    setPagination.startIndex,
    setPagination.startIndex + setPagination.rowsPerPage
  );

  useEffect(() => {
    setIsLoading(true);
    axios
      .get(`${API_BASE_URL.TIPO_OBJETO}/all`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(result => {
        setTiposObjetos(result.data);
      })
      .catch(error => {
        console.error('Erro ao carregar tipos de objeto:', error);
        showDefaultModal('error', 'Erro ao carregar tipos de objeto!');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [atualizar]);

  // Função para lidar com a exclusão
  function handleDelete(tipo) {
    console.log('Chamando showConfirmModal para:', tipo);
    showConfirmModal('delete', () => handleConfirmDelete(tipo));
  }

  function handleConfirmDelete(tipo) {
    axios
      .delete(`${API_BASE_URL.TIPO_OBJETO}/delete/${tipo.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(response => {
        console.log('Exclusão bem-sucedida:', response);
        setAtualizar(prev => !prev);
        setSelectedTipoObjeto(null); // Limpar seleção após exclusão
        showDefaultModal('success', 'Tipo de objeto excluído com sucesso!');
      })
      .catch(error => {
        console.error('Erro ao excluir tipo de objeto:', error);
        console.error('Response data:', error.response?.data);
        console.error('Status:', error.response?.status);
        showDefaultModal(
          'error',
          `Erro ao excluir o tipo de objeto! ${
            error.response?.data?.message || error.message
          }`
        );
      });
  }

  function handleOpenModal(tipoObjeto = null) {
    setEditingTipoObjeto(tipoObjeto);
    setIsModalOpen(true);
  }

  function handleCloseModal() {
    setIsModalOpen(false);
    setEditingTipoObjeto(null);
  }

  function handleModalSuccess() {
    setAtualizar(prev => !prev);
    handleCloseModal();
  }

  const handleRowClick = tipo => {
    setSelectedTipoObjeto(selectedTipoObjeto?.id === tipo.id ? null : tipo);
  };

  return (
    <Layout>
      <ResponsiveShadow>
        <PageHeader title="Tipos de Objeto" icon="bi-tags" closeRoute="/home" backButton={false} />
        <PageContent style={{ paddingBottom: 0 }}>
          <Toolbar
            selectedId={selectedTipoObjeto ? selectedTipoObjeto.id : null}
            onCreate={() => handleOpenModal()}
            onEdit={(id) => { if (id) handleOpenModal(tiposObjetos.find(t => t.id === id)); }}
            onDelete={(id) => { if (id) handleDelete(tiposObjetos.find(t => t.id === id)); }}
            searchValue={filtro}
            onSearch={(v) => setFiltro((v||'').toUpperCase())}
            searchWidth="250px"
          />

      <LoadingModal loading={isLoading} />

      <TableContainer tableClass="table-custom-radius">
        <TableHeader columns={[
          { label: '#', width: '100px' },
          { label: 'Descrição' },
        ]} />
        <tbody>
          {selectedTiposObjetos.length > 0 ? (
            selectedTiposObjetos.map((tipo, index) => (
              <tr
                key={tipo.id}
                className={`cursor-pointer ${
                  selectedTipoObjeto?.id === tipo.id ? 'table-primary' : ''
                }`}
                onClick={() => handleRowClick(tipo)}
                style={{ cursor: 'pointer' }}
              >
                <td>
                  <span className="badge bg-secondary">
                    {setPagination.startIndex + index + 1}
                  </span>
                </td>
                <td>{tipo.descricao}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="2" className="text-center py-4">
                {filtro ? (
                  <>
                    <i className="bi bi-search fa-2x text-muted mb-2 d-block"></i>
                    <p className="text-muted">
                      Nenhum tipo de objeto encontrado para "{filtro}"
                    </p>
                  </>
                ) : (
                  <>
                    <i className="bi bi-tags fa-2x text-muted mb-2 d-block"></i>
                    <p className="text-muted">
                      Nenhum tipo de objeto cadastrado
                    </p>
                  </>
                )}
              </td>
            </tr>
          )}
        </tbody>
      </TableContainer>

      <PageFooter>
        <Pagination10 {...setPagination} />
      </PageFooter>

      {/* Rodapé com informações */}
      {/* {tiposObjetos.length > 0 && (
        <div className="d-flex justify-content-between align-items-center mt-2 px-2">
          <small className="text-muted">
            Mostrando {selectedTiposObjetos.length} de{' '}
            {tiposObjetosFiltrados.length} tipos{' '}
            {tiposObjetosFiltrados.length !== tiposObjetos.length &&
              `(${tiposObjetos.length} total)`}
          </small>
          {selectedTipoObjeto && (
            <small className="text-primary">
              <i className="bi bi-check-circle me-1"></i>
              Selecionado: {selectedTipoObjeto.descricao}
            </small>
          )}
        </div>
      )} */}

      {/* Modal */}
      <TipoObjetoModal
        isOpen={isModalOpen}
        tipoObjeto={editingTipoObjeto}
        onClose={handleCloseModal}
        onSuccess={handleModalSuccess}
      />

      {/* Modals de feedback */}
      <DefaultModal
        show={setDefaultModal.show}
        type={setDefaultModal.type}
        message={setDefaultModal.message}
        onClose={setDefaultModal.onClose}
      />

      <ConfirmModal
        show={setConfirmModal.show}
        action={setConfirmModal.action}
        onConfirm={setConfirmModal.onConfirm}
        onClose={setConfirmModal.onClose}
      />
        </PageContent>
      </ResponsiveShadow>
    </Layout>
  );
}

export default TiposObjetos;
